import React from 'react';
import { TIPOS_TRAMITE_PICKER } from '../utils/rutasTramite';
import { esTramiteActivo } from '../utils/estadoTramite';
import { colorPrimarioTramite } from './Documentos/coloresTramite';

const normalizarTipo = (tipo) => String(tipo || '').trim().toUpperCase();

const posicionTipo = (tipo) => {
    const indice = TIPOS_TRAMITE_PICKER.findIndex((p) => p.nombreBackend === tipo);
    return indice < 0 ? 99 : indice;
};

/**
 * Agrupa los trámites de un cliente por tipo: cuántos hay, cuántos siguen activos y sus estados.
 * Primero los tipos con algún trámite activo, después los cerrados; dentro de cada grupo, el orden
 * de los módulos del sistema.
 * @param {Array<{tipo: string, estado?: string}>} tramites
 */
export function agruparTramitesPorTipo(tramites) {
    const grupos = new Map();
    (tramites || []).forEach((t) => {
        const tipo = normalizarTipo(t?.tipo);
        if (!tipo) return;
        const grupo = grupos.get(tipo) || { tipo, total: 0, activos: 0, estados: [] };
        grupo.total += 1;
        if (esTramiteActivo({ nombreEstadoActual: t.estado })) grupo.activos += 1;
        if (t.estado) grupo.estados.push(t.estado);
        grupos.set(tipo, grupo);
    });
    return [...grupos.values()].sort((a, b) =>
        Number(b.activos > 0) - Number(a.activos > 0) || posicionTipo(a.tipo) - posicionTipo(b.tipo));
}

/**
 * Pastilla de UN tipo de trámite (ícono + etiqueta corta, con el color de su módulo). La usan los
 * chips de Clientes y la columna "Tipo" del listado de trámites, para que se vean igual.
 * @param {{tipo: string, total?: number, cerrado?: boolean, titulo?: string}} props
 */
export function ChipTipoTramite({ tipo, total = 1, cerrado = false, titulo }) {
    const tipoNormalizado = normalizarTipo(tipo);
    const modulo = TIPOS_TRAMITE_PICKER.find((p) => p.nombreBackend === tipoNormalizado);
    const etiqueta = modulo?.etiquetaCorta || tipoNormalizado;
    if (!etiqueta) return <span className="text-muted">—</span>;
    const color = cerrado ? '#94a3b8' : colorPrimarioTramite(modulo?.base?.replace('/', ''));
    return (
        <span
            className="rec-chip-tramite"
            title={titulo || etiqueta}
            style={{
                backgroundColor: cerrado ? '#f1f5f9' : `${color}14`,
                color,
                border: `1px solid ${cerrado ? '#e2e8f0' : `${color}33`}`,
            }}
        >
            {modulo && <i className={modulo.icono} aria-hidden="true" />}
            {etiqueta}
            {total > 1 && <span style={{ fontWeight: 600, opacity: 0.75 }}>×{total}</span>}
        </span>
    );
}

/**
 * Chips con los trámites de un cliente, uno por tipo, con la misma pastilla (`rec-chip-tramite`) que
 * los listados de Recepción y el color de cada módulo. Los tipos sin ningún trámite activo quedan en
 * gris; el detalle de estados va en el tooltip.
 * @param {{tramites: Array<{tipo: string, estado?: string}>, alinear?: string}} props
 */
export default function ChipsTramitesCliente({ tramites, alinear = 'center' }) {
    const grupos = agruparTramitesPorTipo(tramites);
    if (!grupos.length) return <span className="text-muted">—</span>;

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: alinear }}>
            {grupos.map((grupo) => {
                const modulo = TIPOS_TRAMITE_PICKER.find((p) => p.nombreBackend === grupo.tipo);
                const etiqueta = modulo?.etiquetaCorta || grupo.tipo;
                const cerrado = grupo.activos === 0;
                const detalle = grupo.estados.length ? `: ${grupo.estados.join(', ')}` : '';
                const titulo = `${grupo.total} trámite${grupo.total > 1 ? 's' : ''} ${etiqueta}${detalle}${cerrado ? ' (ninguno activo)' : ''}`;
                return <ChipTipoTramite key={grupo.tipo} tipo={grupo.tipo} total={grupo.total} cerrado={cerrado} titulo={titulo} />;
            })}
        </div>
    );
}
